import classNames from "classnames"
import { useDispatch, useSelector } from "react-redux"
import React, { useState } from "react"
import { useForm } from "react-hook-form"
import { toast } from "react-toastify"
import { Checkbox, SignupInput } from "../../../common"
import {
  createIntegrationAction,
  getIntegrationsAction,
} from "../../../../redux/slices/integrationSlice"
import { FormattedMessage, useIntl } from "react-intl"

const AddIntegrationForm = ({ enabledAts }) => {
  const integrations = useSelector((state) => state.integrations)
  const [selected, setSelected] = useState("")
  const { formatMessage } = useIntl()
  const dispatch = useDispatch()

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields, isValid },
    getValues,
    watch,
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      identifier: "",
      apiKey: "",
      agreed: false,
    },
  })

  watch(["apiKey", "email"])

  const values = getValues()

  const onSubmit = async (values) => {
    const res = await dispatch(
      createIntegrationAction({
        apiKey: values.apiKey,
        identifier: values.identifier,
        name: selected,
      }),
    )

    if (!res.error) {
      dispatch(getIntegrationsAction())
      toast.success(formatMessage({ id: "toast.integrationAddedSuccessfully" }))
    } else {
      toast.error(formatMessage({ id: "toast.errorAddingIntegration" }))
    }
  }

  if (selected === "greenhouse") {
    return (
      <form className="integration-form" onSubmit={handleSubmit(onSubmit)}>
        <p className="integration-form__title p">
          <FormattedMessage id="common.logInToGreenhouse" />
        </p>

        <SignupInput
          register={register("identifier", {
            required: formatMessage({ id: "common.identifierRequired" }),
          })}
          placeholder={formatMessage({ id: "common.email" })}
          error={errors.identifier?.message}
          touched={touchedFields.identifier}
          value={values.identifier}
          label={formatMessage({ id: "common.email" })}
        />

        <SignupInput
          register={register("apiKey", {
            required: formatMessage({ id: "common.apiKeyRequired" }),
          })}
          placeholder={formatMessage({ id: "common.apiKey" })}
          error={errors.apiKey?.message}
          touched={touchedFields.apiKey}
          value={values.apiKey}
          label={formatMessage({ id: "common.apiKey" })}
        />

        <Checkbox
          register={register("agreed", {
            required: formatMessage({ id: "common.agreedRequired" }),
            validate: (value) => value === true,
          })}
          label={
            <>
              <FormattedMessage id="common.confirmReadAndAgreed" />{" "}
              <a target={"_blank"} rel={"noreferrer"} href={"/terms-of-use"}>
                <FormattedMessage id="common.termsAndConditionsAndPrivacyPolicies" />
              </a>
            </>
          }
          value={values.agreed}
        />

        <button
          className={classNames("btn btn--small primary", {
            disabled: !isValid || integrations.createIntegrationLoading,
          })}
          onClick={handleSubmit(onSubmit)}
        >
          <FormattedMessage id="common.continue" />
        </button>
      </form>
    )
  }

  if (selected === "lever") {
    return (
      <form className="integration-form" onSubmit={handleSubmit(onSubmit)}>
        <p className="integration-form__title p">
          <FormattedMessage id="common.logInToLever" />
        </p>

        <SignupInput
          register={register("identifier", {
            required: formatMessage({ id: "common.identifierRequired" }),
          })}
          placeholder={formatMessage({ id: "common.email" })}
          error={errors.identifier?.message}
          touched={touchedFields.identifier}
          value={values.identifier}
          label={formatMessage({ id: "common.email" })}
        />

        <SignupInput
          register={register("apiKey", {
            required: formatMessage({ id: "common.apiKeyRequired" }),
          })}
          placeholder={formatMessage({ id: "common.apiKey" })}
          error={errors.apiKey?.message}
          touched={touchedFields.apiKey}
          value={values.apiKey}
          label={formatMessage({ id: "common.apiKey" })}
        />

        <Checkbox
          register={register("agreed", {
            required: formatMessage({ id: "common.agreedRequired" }),
            validate: (value) => value === true,
          })}
          label={
            <>
              <FormattedMessage id="common.confirmReadAndAgreed" />{" "}
              <a target={"_blank"} rel={"noreferrer"} href={"/terms-of-use"}>
                <FormattedMessage id="common.termsAndConditionsAndPrivacyPolicies" />
              </a>
            </>
          }
          value={values.agreed}
        />

        <button
          className={classNames("btn btn--small primary", {
            disabled: !isValid || integrations.createIntegrationLoading,
          })}
          onClick={handleSubmit(onSubmit)}
        >
          <FormattedMessage id="common.continue" />
        </button>
      </form>
    )
  }

  return (
    <div className="integration-form">
      <p className="integration-form__title p">
        <FormattedMessage id="ats.firstConnectToYourATS" />
      </p>

      <div className="integration-form__ats-list">
        {enabledAts.includes("greenhouse") && (
          <div className="integration-form__ats">
            {greenhouseSvg}
            <button
              className={classNames("btn btn--small", {
                selected: selected === "greenhouse",
              })}
              onClick={() => setSelected("greenhouse")}
            >
              <FormattedMessage id="common.connect" />
            </button>
          </div>
        )}
        {enabledAts.includes("lever") && (
          <div className="integration-form__ats">
            <img alt={"Lever"} src={"/img/Lever.png"} />
            <button
              className={classNames("btn btn--small", {
                selected: selected === "lever",
              })}
              onClick={() => setSelected("lever")}
            >
              <FormattedMessage id="common.connect" />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

const greenhouseSvg = (
  <svg width="140" height="32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M11.843 11.421c0 1.372-.58 2.585-1.53 3.534-1.055 1.055-2.585 1.32-2.585 2.216 0 1.213 1.952.844 3.825 2.717 1.24 1.24 2.004 2.875 2.004 4.774 0 3.745-3.007 6.726-6.778 6.726C3.007 31.387 0 28.41 0 24.663c0-1.899.765-3.534 2.005-4.774 1.872-1.872 3.824-1.503 3.824-2.716 0-.897-1.53-1.161-2.585-2.216-.95-.95-1.53-2.163-1.53-3.587 0-2.743 2.242-4.959 4.985-4.959.528 0 1.003.08 1.398.08.713 0 1.082-.317 1.082-.818 0-.29-.132-.66-.132-1.055 0-.897.765-1.636 1.688-1.636.923 0 1.662.765 1.662 1.688 0 .976-.765 1.425-1.345 1.636-.475.158-.845.369-.845.844 0 .894 1.636 1.764 1.636 4.27zm-.528 13.243c0-2.61-1.925-4.721-4.536-4.721-2.612 0-4.537 2.11-4.537 4.721 0 2.585 1.925 4.722 4.537 4.722 2.61 0 4.536-2.14 4.536-4.722zM9.76 11.368c0-1.661-1.345-3.033-2.98-3.033-1.636 0-2.98 1.372-2.98 3.033 0 1.662 1.344 3.034 2.98 3.034 1.635 0 2.98-1.372 2.98-3.034zM21.337 6.673c.844 0 1.477.633 1.477 1.424 0 .791-.607 1.424-1.45 1.424-.871 0-1.346-.553-2.216-.553-.844 0-1.504.606-1.504 1.635v8.308a1.12 1.12 0 11-2.242 0V7.728a1.12 1.12 0 011.108-1.134c1.134 0 .923 1.081 2.057 1.081 1.082 0 1.477-1.002 2.77-1.002zM23.791 13.32c0-3.983 2.743-6.858 6.515-6.858 3.455 0 6.198 2.532 6.198 5.988 0 .817-.527 1.371-1.292 1.371H26.64c-.37 0-.502.132-.502.422 0 .66.528 1.873 1.32 2.664.79.791 1.872 1.266 3.402 1.266 1.134 0 2.136-.422 2.901-.976.237-.184.501-.29.765-.29.554 0 1.002.501 1.002 1.029 0 .316-.105.58-.369.817-1.108.924-2.664 1.425-4.352 1.425-4.088 0-7.016-2.954-7.016-6.858zm9.337-3.798c-.739-.739-1.688-1.108-2.822-1.108-1.134 0-2.19.449-2.954 1.213-.607.607-1.029 1.398-1.029 1.926 0 .264.158.37.501.37h6.858c.316 0 .475-.106.475-.37 0-.633-.501-1.504-1.029-2.031zM38.773 13.32c0-3.983 2.742-6.858 6.514-6.858 3.456 0 6.199 2.532 6.199 5.988 0 .817-.528 1.371-1.293 1.371h-8.572c-.37 0-.501.132-.501.422 0 .66.527 1.873 1.319 2.664.791.791 1.872 1.266 3.402 1.266 1.134 0 2.137-.422 2.901-.976.238-.184.502-.29.765-.29.554 0 1.003.501 1.003 1.029 0 .316-.106.58-.37.817-1.107.924-2.663 1.425-4.352 1.425-4.088 0-7.015-2.954-7.015-6.858zm9.336-3.798c-.738-.739-1.688-1.108-2.822-1.108-1.134 0-2.189.449-2.954 1.213-.606.607-1.029 1.398-1.029 1.926 0 .264.159.37.502.37h6.857c.317 0 .475-.106.475-.37 0-.633-.501-1.504-1.029-2.031zM64.7 20.046a1.126 1.126 0 01-1.135-1.134v-7.174c0-1.873-1.477-3.297-3.376-3.297-1.925 0-3.402 1.45-3.402 3.297v7.174a1.12 1.12 0 11-2.242 0V7.702c0-.607.501-1.108 1.081-1.108 1.108 0 1.055 1.003 1.636 1.003.58 0 1.371-1.135 3.428-1.135 2.875 0 5.117 2.4 5.117 5.276v7.174a1.12 1.12 0 01-1.107 1.134zM79.84 20.046a1.126 1.126 0 01-1.135-1.134v-7.175c0-1.872-1.477-3.297-3.376-3.297-1.925 0-3.402 1.45-3.402 3.297v7.175a1.12 1.12 0 11-2.242 0V1.134a1.12 1.12 0 112.242 0v5.83c0 .316.131.5.474.5.58 0 1.372-1.002 3.43-1.002 2.874 0 5.116 2.4 5.116 5.275v7.175a1.12 1.12 0 01-1.108 1.134zM84.03 13.32c0-3.824 3.06-6.858 6.91-6.858 3.852 0 6.911 3.034 6.911 6.858 0 3.825-3.06 6.858-6.91 6.858-3.849 0-6.91-3.033-6.91-6.858zm11.582 0c0-2.664-1.978-4.853-4.669-4.853-2.664 0-4.642 2.19-4.642 4.853 0 2.664 1.978 4.853 4.642 4.853 2.69 0 4.669-2.189 4.669-4.853zM102.045 6.594c.633 0 1.134.5 1.134 1.134v7.174c0 1.873 1.477 3.297 3.377 3.297 1.925 0 3.402-1.45 3.402-3.297V7.728a1.12 1.12 0 112.242 0v11.21c0 .606-.501 1.107-1.081 1.107-1.108 0-1.055-1.002-1.636-1.002-.58 0-1.371 1.134-3.429 1.134-2.875 0-5.116-2.4-5.116-5.275V7.728c.002-.633.501-1.134 1.107-1.134zM116.182 19.202c-.501-.317-.844-.633-.844-1.24 0-.58.448-1.028 1.002-1.028.66 0 .95.395 1.504.685.685.396 1.477.607 2.453.607 1.793 0 2.69-.818 2.69-1.82 0-1.424-1.319-1.688-3.112-2.163-2.216-.58-4.352-1.24-4.352-3.798 0-2.268 1.74-3.983 4.826-3.983 1.214 0 2.348.317 3.245.87.316.185.633.581.633 1.03 0 .58-.475 1.08-1.082 1.08-.396 0-.712-.263-1.108-.527-.395-.263-.976-.5-1.767-.5-1.767 0-2.532.764-2.532 1.793 0 1.345 1.293 1.661 3.139 2.11 2.163.527 4.299 1.213 4.299 3.824 0 2.269-1.688 4.036-4.932 4.036-1.583 0-3.007-.317-4.062-.976zM127.287 13.32c0-3.983 2.743-6.858 6.515-6.858 3.455 0 6.198 2.532 6.198 5.988 0 .817-.527 1.371-1.292 1.371h-8.572c-.37 0-.501.132-.501.422 0 .66.527 1.873 1.318 2.664.792.791 1.873 1.266 3.403 1.266 1.134 0 2.136-.422 2.901-.976.238-.184.501-.29.765-.29.554 0 1.002.501 1.002 1.029 0 .316-.105.58-.369.817-1.108.924-2.664 1.425-4.352 1.425-4.086 0-7.016-2.954-7.016-6.858zm9.34-3.798c-.739-.739-1.688-1.108-2.822-1.108-1.135 0-2.19.449-2.955 1.213-.606.607-1.028 1.398-1.028 1.926 0 .264.158.37.501.37h6.858c.316 0 .474-.106.474-.37 0-.633-.501-1.504-1.028-2.031z"
      fill="#008561"
    />
  </svg>
)

export default AddIntegrationForm
